import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  ShieldCheck, 
  DollarSign, 
  CheckCircle2, 
  Clock, 
  XCircle,
  FileText,
  Search,
  RefreshCw,
  Download,
  TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [paymentsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/payments`, {
          params: statusFilter !== "all" ? { status: statusFilter } : {}
        }),
        axios.get(`${API}/admin/stats`)
      ]);
      
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const filteredPayments = payments.filter(payment => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      payment.patient_name?.toLowerCase().includes(search) ||
      payment.email?.toLowerCase().includes(search) ||
      payment.checkout_reference?.toLowerCase().includes(search) ||
      payment.patient_id?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "PAID":
        return <Badge className="status-badge status-paid">Paid</Badge>;
      case "PENDING":
        return <Badge className="status-badge status-pending">Pending</Badge>;
      case "PENDING_VERIFICATION":
        return <Badge className="status-badge bg-blue-50 text-blue-700 border border-blue-200">Awaiting Verification</Badge>;
      case "FAILED":
      case "CANCELLED":
        return <Badge className="status-badge status-failed">{status}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const exportToCSV = () => {
    const headers = ["Date", "Patient", "Email", "Reference", "Status", "Form Released"];
    const rows = filteredPayments.map(p => [
      formatDate(p.created_at),
      p.patient_name,
      p.email,
      p.checkout_reference,
      p.status,
      p.form_released ? "Yes" : "No"
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  };

  const verifyPayment = async (checkoutId) => {
    try {
      await axios.post(`${API}/payments/mock-complete/${checkoutId}`);
      toast.success("Payment verified successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to verify payment");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-sky-500" />
              <div>
                <h1 className="font-bold text-xl text-slate-900">Protea Health Admin</h1>
                <p className="text-sm text-slate-500">Payment Management Dashboard</p>
              </div>
            </div>
            <Button 
              onClick={fetchData} 
              variant="outline" 
              size="sm"
              disabled={isLoading}
              data-testid="btn-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-elevated" data-testid="stat-total">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Payments</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_payments || 0}</p>
                </div>
                <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-successful">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Successful</p>
                  <p className="text-3xl font-bold text-emerald-600">{stats?.successful_payments || 0}</p>
                </div>
                <div className="h-12 w-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-revenue">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-3xl font-bold text-slate-900">
                    £{((stats?.total_revenue || 0) / 100).toFixed(0)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-sky-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elevated" data-testid="stat-forms">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Forms Released</p>
                  <p className="text-3xl font-bold text-slate-900">{stats?.forms_released || 0}</p>
                </div>
                <div className="h-12 w-12 bg-violet-50 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payments Table */}
        <Card className="card-elevated" data-testid="payments-table-card">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-xl text-slate-900">Payment Records</CardTitle>
              
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search payments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-full sm:w-64"
                    data-testid="search-input"
                  />
                </div>
                
                {/* Status filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="PAID">Paid</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="FAILED">Failed</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Export */}
                <Button 
                  variant="outline" 
                  onClick={exportToCSV}
                  data-testid="btn-export"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="spinner" />
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No payments found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Form Released</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.map((payment) => (
                      <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDate(payment.created_at)}
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          {payment.patient_name || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {payment.email}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {payment.checkout_reference}
                          </code>
                        </TableCell>
                        <TableCell className="font-medium">
                          £{(payment.amount / 100).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(payment.status)}
                        </TableCell>
                        <TableCell>
                          {payment.form_released ? (
                            <span className="flex items-center gap-1 text-emerald-600 text-sm">
                              <CheckCircle2 className="h-4 w-4" />
                              Yes
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-slate-400 text-sm">
                              <Clock className="h-4 w-4" />
                              No
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {payment.status === "PENDING_VERIFICATION" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              onClick={() => verifyPayment(payment.checkout_id)}
                              data-testid={`btn-verify-${payment.id}`}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Verify
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
